import { useMemo } from 'react';

import { useEventInfo } from '@shared/utils/eventInfo';
import { calculateStayDates } from '@shared/utils/dateHelpers';
import { useHotelRoutePaths } from '@shared/utils/accommodations/hotelRoutes';
import type { UtmParameters } from '@shared/core/router.helper/RoutesContext';
import { buildCarSearchParams } from '@shared/utils/accommodations/carSearchParams';

/** Builds the `/:eventHandle/car/search` URL prefilled from event info with the given UTM parameters. */
export const useEventCarSearchUrl = (utm: UtmParameters): string | undefined => {
  const { eventHandle, eventInfo } = useEventInfo();

  const hotelRoutePaths = useHotelRoutePaths();
  const finalizedDateString = eventInfo?.finalizedDate?.dateString;
  const locationInfo = eventInfo?.locationInfo;

  return useMemo(() => {
    if (!eventHandle) {
      return undefined;
    }

    // Uses ±1 day around the event date, defaulting to 1 month out if date is missing or past.
    const { checkIn, checkOut } = calculateStayDates(finalizedDateString);

    return hotelRoutePaths.carSearch.buildRelativeUrl(
      eventHandle,
      buildCarSearchParams({ checkIn: checkIn.dateString, checkOut: checkOut.dateString, destination: locationInfo, utm }),
    );
  }, [eventHandle, finalizedDateString, locationInfo, hotelRoutePaths, utm]);
};
